/**
 * Settings, and the storage screen in particular.
 *
 * An app that downloads audio owes the user a number in megabytes and a way to cap it.
 * The honest part is harder than the slider: browser storage is a revocable quota, and
 * Safari will evict an origin that has not been opened for a while, so "available
 * offline" is a claim that has to be checked rather than remembered. This screen reports
 * what is actually resident, not what we once downloaded.
 */

import { DONATE_URL } from '../links';
import { buildStamp } from './build-info';
import { InstallSetting } from './Install';
import { useEffect, useState } from 'preact/hooks';
import {
  STORAGE_PRESETS, formatBytes, resetSettings, settings, updateSettings,
} from '../state/settings';
import { clearHistory, history } from '../state/history';
import { accountSession } from '../account/session';
import { anomalyTotals, trace } from '../audio/trace';
import { ByteStore } from '../audio/sources';
import { isNativeShell } from '../config/origin';
import { Brahm } from '../audio/native-bridge';
import { SpeechSharePreference } from './SpeechSharePreference';

interface Props { onBack: () => void; onDiagnostics: () => void; onAccount: () => void }

interface Usage {
  cachedBytes: number;
  cachedEntries: number;
  quotaBytes: number | null;
  usedBytes: number | null;
  persisted: boolean | null;
}

export function Settings({ onBack, onDiagnostics, onAccount }: Props) {
  const s = settings.value;
  const session = accountSession.value;
  const [usage, setUsage] = useState<Usage | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (isNativeShell()) {
      const native = await Brahm.cacheUsage();
      const web = await new ByteStore().size();
      setUsage({
        cachedBytes: native.cachedBytes + web.bytes,
        cachedEntries: native.cachedEntries + web.entries,
        quotaBytes: null, usedBytes: null, persisted: true,
      });
      return;
    }
    const store = new ByteStore();
    const { bytes, entries } = await store.size();
    let quotaBytes: number | null = null;
    let usedBytes: number | null = null;
    let persisted: boolean | null = null;
    try {
      const est = await navigator.storage.estimate();
      quotaBytes = est.quota ?? null;
      usedBytes = est.usage ?? null;
      persisted = await navigator.storage.persisted();
    } catch { /* not available in this browser */ }
    setUsage({ cachedBytes: bytes, cachedEntries: entries, quotaBytes, usedBytes, persisted });
  };

  useEffect(() => { void refresh(); }, []);

  const askPersist = async () => {
    setBusy(true);
    try { await navigator.storage.persist(); } catch { /* declined or unsupported */ }
    await refresh();
    setBusy(false);
  };

  const clearAudio = async () => {
    setBusy(true);
    if (isNativeShell()) await Promise.all([Brahm.clearCache(), new ByteStore().clear()]);
    else await new ByteStore().clear();
    await refresh();
    setBusy(false);
  };

  // Counted here rather than inside the diagnostics screen so the row can say whether
  // there is anything to look at before someone taps into it.
  const live = trace.snapshot();
  const flagged = Object.values(
    anomalyTotals(live ? [live, ...trace.recent()] : trace.recent()),
  ).reduce((a, n) => a + (n ?? 0), 0);

  const pct = usage && s.storageBudgetBytes > 0
    ? Math.min(100, (usage.cachedBytes / s.storageBudgetBytes) * 100)
    : 0;

  return (
    <div class="screen">
      <div class="shell stack" style={{ flex: 1 }}>
        <button class="btn btn-quiet" onClick={onBack}
                style={{ alignSelf: 'flex-start', minHeight: '2.5rem', paddingLeft: 0 }}>
          ← Back
        </button>

        <h1>Settings</h1>

        {/* ------------------------------------------------------ account */}
        <section class="stack-sm">
          <h3 class="eyebrow">Account</h3>
          <div class="spread">
            <span>
              {session ? session.user.username : 'Account'}
              <br />
              <span class="dim" style={{ fontSize: 'var(--step--1)' }}>
                {session
                  ? 'This device is signed in. Sitting does not depend on it.'
                  : 'Optional. Sitting never needs one.'}
              </span>
            </span>
            <button class="btn" onClick={onAccount}>Open</button>
          </div>
        </section>

        <hr />

        {/* ------------------------------------------------------ storage */}
        <section class="stack-sm">
          <h3 class="eyebrow">Audio kept on this phone</h3>
          <div class="spread">
            <span class="fig" style={{ fontSize: 'var(--step-2)' }}>
              {formatBytes(usage?.cachedBytes ?? 0)}
            </span>
            <span class="dim fig" style={{ fontSize: 'var(--step--1)' }}>
              of {formatBytes(s.storageBudgetBytes)} allowed
            </span>
          </div>
          <div style={{ height: '3px', background: 'var(--rule)' }}>
            <div style={{ height: '3px', width: `${pct}%`, background: 'var(--accent-fill)' }} />
          </div>
          <p class="dim" style={{ fontSize: 'var(--step--1)' }}>
            Only his speech is downloaded — the silences are made on your phone and cost
            nothing. A typical session is about 3 MB.
            {usage?.cachedEntries ? ` ${usage.cachedEntries} files stored.` : ''}
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--gap-1)' }}>
            {STORAGE_PRESETS.map((p) => (
              <button
                key={p.bytes}
                class="chip"
                aria-pressed={s.storageBudgetBytes === p.bytes}
                onClick={() => {
                  updateSettings({ storageBudgetBytes: p.bytes });
                  setBusy(true);
                  const trim = isNativeShell()
                    ? Promise.all([Brahm.clearCache(), new ByteStore().trim(p.bytes)]).then(() => undefined)
                    : new ByteStore().trim(p.bytes);
                  void trim.then(refresh).finally(() => setBusy(false));
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          {usage?.quotaBytes != null && (
            <p class="dim fig" style={{ fontSize: 'var(--step--1)' }}>
              This browser offers {formatBytes(usage.quotaBytes)} in total;
              {' '}{formatBytes(usage.usedBytes ?? 0)} is in use by this app.
            </p>
          )}

          {!isNativeShell() && <div class="spread">
            <span>
              Protect from automatic clearing
              <br />
              <span class="dim" style={{ fontSize: 'var(--step--1)' }}>
                {usage?.persisted
                  ? 'Granted. Downloads will survive.'
                  : 'Not granted. The browser may clear downloads if space runs low '
                    + 'or the app goes unopened for a while.'}
              </span>
            </span>
            {!usage?.persisted && (
              <button class="btn" disabled={busy} onClick={askPersist}>Ask</button>
            )}
          </div>}

          <button class="btn" disabled={busy} onClick={clearAudio}
                  style={{ alignSelf: 'flex-start' }}>
            Delete downloaded audio
          </button>
        </section>

        <hr />

        <SpeechSharePreference />

        <hr />

        {/* ------------------------------------------------------ during a sit */}
        <section class="stack-sm">
          <h3 class="eyebrow">During a sit</h3>
          <Toggle
            label="Dim the screen"
            hint="The screen goes dark a few seconds in. Tap to see the time remaining."
            value={s.dimDuringSit}
            onChange={(v) => updateSettings({ dimDuringSit: v })}
          />
          <Toggle
            label="Keep the phone awake"
            hint="Stops the device sleeping mid-sit. Uses a little more battery."
            value={s.keepAwake}
            onChange={(v) => updateSettings({ keepAwake: v })}
          />
        </section>

        <hr />

        {/* ------------------------------------------------------ appearance */}
        <section class="stack-sm">
          <h3 class="eyebrow">Appearance</h3>
          <div class="row" style={{ gap: 'var(--gap-1)' }}>
            {(['system', 'light', 'dark'] as const).map((t) => (
              <button key={t} class="chip" aria-pressed={s.theme === t}
                      onClick={() => updateSettings({ theme: t })}>
                {t[0].toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <p class="dim" style={{ fontSize: 'var(--step--1)' }}>
            The sit itself is always black, whichever you choose.
          </p>
        </section>

        <hr />

        {/* ------------------------------------------------------ privacy */}
        <section class="stack-sm">
          <h3 class="eyebrow">Privacy</h3>
          <Toggle
            label="Send anonymous diagnostics"
            hint="Whether audio stalled, which format your device chose, and how far into
                  sits people get. No identifier of any kind, no account, no IP kept,
                  nothing about which recordings you choose. On by default, because it is
                  the only way to tell whether the audio engine works on phones we do not
                  own. Turn it off here and it stops immediately."
            value={s.telemetryEnabled}
            onChange={(v) => updateSettings({ telemetryEnabled: v, telemetryChosen: true })}
          />
          <Toggle
            label="Upload session traces"
            hint="Uploads each sit's detailed trace, including its recording title,
                  recording ID, and this device's full browser description. On by default
                  so an interrupted sit can be read after the app restarts. Turn it off
                  here to stop automatic uploads."
            value={s.uploadDiagnostics}
            onChange={(v) => updateSettings({
              uploadDiagnostics: v,
              uploadDiagnosticsChosen: true,
            })}
          />
          <Toggle
            label="Upload full diagnostics after every sit"
            hint="After each sit, uploads the complete diagnostic log this device still
                  holds, not only the sit that just ended. This happens after every sit
                  while session-trace uploads are on. Turn it off to upload only that
                  sit, as before."
            value={s.uploadFullLog}
            onChange={(v) => updateSettings({
              uploadFullLog: v,
              uploadFullLogChosen: true,
            })}
          />
          <a class="btn btn-quiet" href="/privacy/"
             style={{ alignSelf: 'flex-start', textDecoration: 'none' }}>
            Privacy policy
          </a>
          <div class="spread">
            <span>
              What you have sat with
              <br />
              <span class="dim fig" style={{ fontSize: 'var(--step--1)' }}>
                {history.value.length} session{history.value.length === 1 ? '' : 's'},
                {session ? ' on this phone and on the account' : ' stored only on this phone'}
              </span>
            </span>
            <button class="btn" onClick={() => confirm(
              'Clear your history? The app will stop avoiding recordings and stories '
              + 'you have heard recently.') && clearHistory()}>
              Clear
            </button>
          </div>

          {/* Deliberately one tap from Settings rather than behind another gesture:
              someone who has just heard a gap needs to reach this while they still know
              which minute it was in. The automatic route above and the explicit send on
              this screen are stated separately because they are different instructions. */}
          <div class="spread">
            <span>
              If a sit sounded wrong
              <br />
              <span class="dim fig" style={{ fontSize: 'var(--step--1)' }}>
                {flagged > 0
                  ? `${flagged} thing${flagged === 1 ? '' : 's'} flagged in your recent sits`
                  : 'A record of your last few sits, stored here and uploaded when enabled'}
              </span>
            </span>
            <button class="btn" onClick={onDiagnostics}>Diagnostics</button>
          </div>
        </section>

        <hr />

        <section class="stack-sm">
          <h3 class="eyebrow">This app</h3>
          <InstallSetting />
        </section>

        <hr />

        <section class="stack-sm">
          <h3 class="eyebrow">About</h3>
          <p class="dim selectable" style={{ fontSize: 'var(--step--1)' }}>
            These are real recordings of Ajahn Brahm, used with the permission of the
            Buddhist Society of Western Australia. Every retained passage plays at its
            recorded speed and in source order. Cadence and reviewed edits can leave out
            declared passages; the preview shows what remains. Added silence is generated
            to match the room he was sitting in.
          </p>
          <a class="btn" href={DONATE_URL} target="_blank" rel="noreferrer"
             style={{ alignSelf: 'flex-start', textDecoration: 'none' }}>
            Donate to BSWA
          </a>
          <button class="btn btn-quiet" style={{ alignSelf: 'flex-start' }}
                  onClick={() => confirm('Reset all settings to their defaults?')
                    && resetSettings()}>
            Reset settings
          </button>
          {/* Which build is actually running. See build-info.ts for why this is here. */}
          <p class="dim selectable" data-testid="build-stamp"
             style={{ fontSize: 'var(--step--1)', fontVariantNumeric: 'tabular-nums' }}>
            {buildStamp()}
          </p>
        </section>
      </div>
    </div>
  );
}

function Toggle({ label, hint, value, onChange }: {
  label: string; hint: string; value: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label class="spread" style={{ cursor: 'pointer', alignItems: 'flex-start' }}>
      <span>
        {label}
        <br />
        <span class="dim" style={{ fontSize: 'var(--step--1)' }}>{hint}</span>
      </span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange((e.target as HTMLInputElement).checked)}
        style={{ width: '1.4rem', height: '1.4rem', accentColor: 'var(--accent-fill)',
                 marginTop: '0.2rem', flexShrink: 0 }}
      />
    </label>
  );
}
